// Export PDF del Piano Economico. Genera un documento stampabile con:
//   - Header con dati immobile
//   - Tabella scenari (proposta, manodopera, cost lines, materiali, imprevisti,
//     tasse registro, ricavi, utile lordo, ROI, ROE, utile netto)
//   - Tabella dettaglio materiali (qtà × €/unità = totale)
//   - Riepilogo KPI su 3 scenari
//
// Usa jspdf + jspdf-autotable (già in package.json).

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtPct = (n) => `${(Number(n) || 0).toFixed(2)}%`;
const fmtNum = (n, d = 0) =>
  new Intl.NumberFormat("it-IT", { maximumFractionDigits: d }).format(Number(n) || 0);

const matPrezzo = (m) => (Number(m?.prezzo) > 0 ? Number(m.prezzo) : Number(m?.prezzo_a) || 0);
const matQty    = (m) => (m?.quantita === undefined || m?.quantita === null) ? 1 : Number(m.quantita);

const SCEN = [
  { key: "peggiore", label: "PEGGIORE" },
  { key: "medio",    label: "MEDIO" },
  { key: "migliore", label: "MIGLIORE" },
];

export const exportPianoPdf = ({ plan, econ, derived }) => {
  const p = plan.property || {};
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  const margin = 40;
  let y = margin;

  // ─── Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Piano Economico", margin, y);
  y += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80);
  const titleLine = `${p.indirizzo || "—"} ${p.civico || ""} · ${p.citta || "—"}${p.macro_area ? ` · ${p.macro_area}` : ""}`;
  doc.text(titleLine, margin, y);
  y += 14;

  doc.text(
    `Sup: ${fmtNum(p.mq)} m²   ·   Prezzo pubbl.: ${fmtEuro(p.prezzo)}   ·   €/m²: ${fmtEuro(p.eur_mq)}   ·   Generato: ${new Date().toLocaleString("it-IT")}`,
    margin, y
  );
  y += 20;
  doc.setTextColor(0);

  // ─── Tabella Scenari
  const impPct = Number(econ.imprevisti_pct) || 0;
  const sostPct = Number(econ.sostitutiva_pct) || 0;
  const pubPct = Number(econ.pubblicita_pct) || 0;
  const scenBody = [];

  // Proposta
  scenBody.push(["Ns. Proposta (acquisto)",
    fmtEuro(econ.proposta?.peggiore), fmtEuro(econ.proposta?.medio), fmtEuro(econ.proposta?.migliore),
  ]);
  scenBody.push(["Manodopera",
    fmtEuro(econ.manodopera?.peggiore), fmtEuro(econ.manodopera?.medio), fmtEuro(econ.manodopera?.migliore),
  ]);
  (econ.cost_lines || []).forEach((l) => {
    scenBody.push([l.label || "—", fmtEuro(l.peggiore), fmtEuro(l.medio), fmtEuro(l.migliore)]);
  });
  scenBody.push([
    { content: "Materiali (auto)", styles: { fillColor: [245, 240, 255] } },
    { content: fmtEuro(derived.matTot), styles: { halign: "right", fillColor: [245, 240, 255] } },
    { content: fmtEuro(derived.matTot), styles: { halign: "right", fillColor: [245, 240, 255] } },
    { content: fmtEuro(derived.matTot), styles: { halign: "right", fillColor: [245, 240, 255] } },
  ]);
  scenBody.push([
    `Imprevisti (${fmtNum(impPct, 1)}% manodopera)`,
    fmtEuro((Number(econ.manodopera?.peggiore) || 0) * impPct / 100),
    fmtEuro((Number(econ.manodopera?.medio)    || 0) * impPct / 100),
    fmtEuro((Number(econ.manodopera?.migliore) || 0) * impPct / 100),
  ]);
  scenBody.push([
    `Tasse registro (${econ.tipo_acquirente === "srl" ? "9% proposta" : econ.tipo_acquirente === "2casa" ? "2°casa" : "1°casa"})`,
    fmtEuro(derived.tasse), fmtEuro(derived.tasse), fmtEuro(derived.tasse),
  ]);
  scenBody.push([
    { content: "TOT. Spese + Acquisto", styles: { fontStyle: "bold" } },
    { content: fmtEuro(derived.totSpese.peggiore), styles: { halign: "right", fontStyle: "bold" } },
    { content: fmtEuro(derived.totSpese.medio),    styles: { halign: "right", fontStyle: "bold" } },
    { content: fmtEuro(derived.totSpese.migliore), styles: { halign: "right", fontStyle: "bold" } },
  ]);
  scenBody.push(["Ricavi (prezzo di vendita)",
    fmtEuro(econ.ricavi?.peggiore), fmtEuro(econ.ricavi?.medio), fmtEuro(econ.ricavi?.migliore),
  ]);
  scenBody.push([
    `Prezzo pubblicità (+${fmtNum(pubPct, 1)}%)`,
    fmtEuro(derived.pubblicita.peggiore), fmtEuro(derived.pubblicita.medio), fmtEuro(derived.pubblicita.migliore),
  ]);
  scenBody.push([
    { content: "Utile Lordo", styles: { fontStyle: "bold" } },
    { content: fmtEuro(derived.utileLordo.peggiore), styles: { halign: "right", fontStyle: "bold" } },
    { content: fmtEuro(derived.utileLordo.medio),    styles: { halign: "right", fontStyle: "bold" } },
    { content: fmtEuro(derived.utileLordo.migliore), styles: { halign: "right", fontStyle: "bold" } },
  ]);
  scenBody.push(["ROI (utile / spese)",
    fmtPct(derived.roi.peggiore), fmtPct(derived.roi.medio), fmtPct(derived.roi.migliore),
  ]);
  scenBody.push([
    `ROE (esposizione ${fmtEuro(econ.esposizione)})`,
    fmtPct(derived.roe.peggiore), fmtPct(derived.roe.medio), fmtPct(derived.roe.migliore),
  ]);
  scenBody.push([
    `Utile netto (senza residenza · imposta ${fmtNum(sostPct, 1)}%)`,
    fmtEuro(derived.utileNettoSenzaResid.peggiore),
    fmtEuro(derived.utileNettoSenzaResid.medio),
    fmtEuro(derived.utileNettoSenzaResid.migliore),
  ]);

  autoTable(doc, {
    startY: y,
    head: [["Voce", "PEGGIORE", "MEDIO", "MIGLIORE"]],
    body: scenBody,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8.5, cellPadding: 4 },
    headStyles: { fillColor: [30, 41, 59], textColor: 255, halign: "left" },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { halign: "right", cellWidth: 90 },
      2: { halign: "right", cellWidth: 90 },
      3: { halign: "right", cellWidth: 90 },
    },
  });
  y = doc.lastAutoTable.finalY + 24;

  // ─── Dettaglio materiali
  const mats = econ.materials || [];
  if (mats.length > 0) {
    if (y > 700) { doc.addPage(); y = margin; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Dettaglio materiali", margin, y);
    y += 8;

    const matBody = mats.map((m) => {
      const qty = matQty(m);
      const prezzo = matPrezzo(m);
      return [m.label || "—", fmtNum(qty, 2), fmtEuro(prezzo), fmtEuro(qty * prezzo)];
    });
    matBody.push([
      { content: "TOTALE MATERIALI", styles: { fontStyle: "bold" } },
      { content: "", styles: {} },
      { content: "", styles: {} },
      { content: fmtEuro(derived.matTot), styles: { halign: "right", fontStyle: "bold" } },
    ]);

    autoTable(doc, {
      startY: y + 6,
      head: [["Voce materiali", "Qtà", "€/unità", "Totale"]],
      body: matBody,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8.5, cellPadding: 4 },
      headStyles: { fillColor: [113, 63, 173], textColor: 255, halign: "left" },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { halign: "right", cellWidth: 70 },
        2: { halign: "right", cellWidth: 90 },
        3: { halign: "right", cellWidth: 100 },
      },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // ─── Riepilogo KPI 3 scenari
  if (y > 700) { doc.addPage(); y = margin; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Riepilogo KPI", margin, y);
  y += 8;

  const kpiBody = SCEN.map((s) => [
    s.label,
    fmtEuro(derived.totSpese[s.key]),
    fmtEuro(derived.utileLordo[s.key]),
    fmtPct(derived.roi[s.key]),
    fmtPct(derived.roe[s.key]),
    fmtEuro(derived.utileNettoSenzaResid[s.key]),
  ]);

  autoTable(doc, {
    startY: y + 6,
    head: [["Scenario", "Tot. Spese", "Utile Lordo", "ROI", "ROE", "Utile Netto"]],
    body: kpiBody,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [10, 132, 255], textColor: 255 },
    columnStyles: {
      0: { fontStyle: "bold" },
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
    },
  });

  // ─── Footer con data
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      `ImmoMap · Piano Economico · pag. ${i}/${pageCount}`,
      margin,
      doc.internal.pageSize.height - 20
    );
  }

  const safeName = (p.indirizzo || "piano")
    .toString()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .toLowerCase()
    .slice(0, 40);
  doc.save(`piano_economico_${safeName}.pdf`);
};
